/**
 * Tenant Branding Hook - Production Implementation
 *
 * Queries TenantBranding from GraphQL API (DynamoDB backend).
 * This hook provides industry-specific terminology and branding configuration.
 *
 * FERP Principles:
 * - Data-driven: Terminology comes from backend, NOT hardcoded
 * - Repository pattern: All data via GraphQL → TenantBrandingRepository
 * - Dumb frontend: UI adapts to whatever backend returns
 *
 * Usage:
 * ```tsx
 * const { branding, translateTerm, loading, error } = useTenantBranding(tenantId);
 *
 * // Get industry-specific terminology
 * const label = translateTerm('workflow'); // "Clinical Pathway" for healthcare
 * ```
 */

import { useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER hook, allowed to use Apollo Client directly
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// GraphQL Query - matches backend TenantBrandingQueries.get_tenant_branding
const GET_TENANT_BRANDING = gql`
  query GetTenantBranding($tenantId: String!) {
    getTenantBranding(tenantId: $tenantId) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      industry
      complianceFrameworks
      features {
        dualApproval
        lifeCritical
        auditTrail
        realTimeAlerts
        documentGeneration
      }
      safetyConfig {
        defaultSafetyLevel
        requireConfirmationAbove
        autoEscalationEnabled
      }
      createdAt
      updatedAt
      version
    }
  }
`;

// Types matching backend GqlTenantBranding
export interface TenantFeatures {
  dualApproval: boolean;
  lifeCritical: boolean;
  auditTrail: boolean;
  realTimeAlerts: boolean;
  documentGeneration: boolean;
}

export interface TenantSafetyConfig {
  defaultSafetyLevel: string;
  requireConfirmationAbove: string;
  autoEscalationEnabled: boolean;
}

export interface TenantBranding {
  tenantId: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  companyName: string;
  companyTagline: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  websiteUrl: string | null;
  terminology: Record<string, string>;
  customDomain: string | null;
  subdomain: string;
  // Public website fields (DRR-107)
  websiteSlug: string | null;
  address: string | null;
  hours: string | null;
  licenseType: string | null;
  services: string[];
  accreditations: string[];
  hideFerpBranding: boolean;
  showHelpCenter: boolean;
  showSupportChat: boolean;
  industry: string;
  complianceFrameworks: string[];
  features: TenantFeatures;
  safetyConfig: TenantSafetyConfig;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface UseTenantBrandingResult {
  branding: TenantBranding | null;
  loading: boolean;
  error: Error | null;
  translateTerm: (term: string) => string;
  refetch: () => Promise<void>;
}

/**
 * Production hook for tenant branding from GraphQL API.
 *
 * @param tenantId - Tenant ID to fetch branding for
 */
export function useTenantBranding(tenantId: string | null): UseTenantBrandingResult {
  const skipQuery = !tenantId;

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch: refetchQuery,
  } = useQuery<Record<string, any>>(GET_TENANT_BRANDING, {
    variables: { tenantId: tenantId ?? '' },
    skip: skipQuery,
    fetchPolicy: 'cache-and-network',
  });

  // Get branding from query result
  const branding = useMemo((): TenantBranding | null => {
    if (skipQuery) return null;
    if (data?.getTenantBranding) {
      // Parse terminology JSON if needed
      const raw = data?.getTenantBranding;
      return {
        ...raw,
        terminology: typeof raw.terminology === 'string'
          ? JSON.parse(raw.terminology)
          : raw.terminology ?? {},
      };
    }
    return null;
  }, [data, skipQuery]);

  // Translate FERP term to industry-specific term
  const translateTerm = useCallback(
    (term: string): string => {
      if (!branding?.terminology) return term;
      const lowerTerm = term.toLowerCase();
      return branding.terminology[lowerTerm] || term;
    },
    [branding]
  );

  // Refetch function
  const refetch = useCallback(async () => {
    if (!skipQuery) {
      await refetchQuery();
    }
  }, [refetchQuery, skipQuery]);

  return {
    branding,
    loading: !skipQuery && queryLoading,
    error: skipQuery ? null : (queryError as Error | null),
    translateTerm,
    refetch,
  };
}

export default useTenantBranding;
